import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { authAPI, ticketAPI, configAPI, adminMgmtAPI } from '@/lib/api';
import { ArrowLeft, User, Mail, Phone, Building, AlertTriangle, AlertCircle, MessageSquare, Star, Shuffle, Loader2, CheckCircle2, Trash2 } from 'lucide-react';
import SLATimer from '@/components/SLATimer';

const TicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [comment, setComment] = useState('');
  const [delayReason, setDelayReason] = useState('');
  const [newExpectedDate, setNewExpectedDate] = useState('');
  const [estimatedCost, setEstimatedCost] = useState('');
  const [delayReasons, setDelayReasons] = useState([]);
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [onLeaveData, setOnLeaveData] = useState({
    subordinate_name: '',
    subordinate_email: '',
    work_description: ''
  });
  const [shiftCompletedData, setShiftCompletedData] = useState({
    next_shift_name: '',
    next_shift_email: '',
    comment: ''
  });
  const [materialData, setMaterialData] = useState({
    material_name: '',
    estimated_cost: '',
    required_by: '',
    decision: '',
    decision_remark: ''
  });
  const [duplicateData, setDuplicateData] = useState({ description: '' });
  const [tatHours, setTatHours] = useState('');
  const [savingTat, setSavingTat] = useState(false);
  const [divertData, setDivertData] = useState({
    target_department: '',
    resolver_name: '',
    resolver_email: '',
    work_description: '',
    expected_resolution_datetime: ''
  });
  const [divertDepartments, setDivertDepartments] = useState([]);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);

  const [loadError, setLoadError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [userRes, ticketRes, reasonsRes] = await Promise.all([
        authAPI.getMe(),
        ticketAPI.getTicket(ticketId),
        configAPI.getDelayReasons()
      ]);
      setUser(userRes.data);
      setTicket(ticketRes.data);
      setDelayReasons(reasonsRes.data);
      setNewStatus(ticketRes.data.status);
      setTatHours(ticketRes.data.resolution_tat_hours ? String(ticketRes.data.resolution_tat_hours) : '');
      setLoadError(null);
    } catch (error) {
      // Distinguish error classes for a better UX:
      //   • 404 — ticket never existed OR was archival-closed for non-admins.
      //     Take the user back gracefully with a friendly toast.
      //   • 403 — access denied (e.g., a resolver opening a ticket outside
      //     their department). Show a clear in-page banner with retry +
      //     back, no navigation away.
      //   • 5xx / network — surface a retry banner; keep the page mounted
      //     so any in-flight edits are not lost.
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (process.env.NODE_ENV !== 'production') {
        console.debug('TicketDetail loadData failed:', status, detail || error.message);
      }
      if (status === 404) {
        toast.error(detail || 'This ticket is no longer available.');
        navigate(-1);
      } else if (status === 403) {
        setLoadError({
          type: 'forbidden',
          message: detail || 'You do not have permission to view this ticket.',
        });
      } else {
        setLoadError({
          type: 'transient',
          message: detail || 'We could not load this ticket right now. Please retry.',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [ticketId, navigate]);

  // Load divert departments list once
  useEffect(() => {
    configAPI.getDivertDepartments()
      .then(res => setDivertDepartments(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // When the resolver/admin picks "Purchase Material Approved", auto-fetch
  // the material details the original requester filled in earlier so the
  // approver only has to pick a decision. No re-entry, no glitches.
  useEffect(() => {
    if (newStatus === 'Purchase Material Approved' && ticket) {
      setMaterialData(prev => ({
        ...prev,
        material_name: ticket.material_name || prev.material_name || '',
        estimated_cost: ticket.material_estimated_cost != null
          ? String(ticket.material_estimated_cost)
          : (prev.estimated_cost || ''),
        required_by: ticket.material_required_by || prev.required_by || '',
      }));
    }
  }, [newStatus, ticket]);

  // Approver-eligibility derived from the logged-in user (admin / Store PO
  // / Accounts PO). Backend re-enforces this — UI just hides the submit.
  const isMaterialApprover = (() => {
    if (!user) return false;
    if (user.role === 'cao') return true;
    if (user.is_caredesk_owner) return true;
    const dept = (user.department || '').toLowerCase();
    if (dept === 'store dept' || dept === 'accounts') return true;
    const depts = (user.departments || []).map(d => (d || '').toLowerCase());
    return depts.includes('store dept') || depts.includes('accounts');
  })();

  const handleUpdate = async () => {
    if (newStatus === 'SLA BREACHED' && !delayReason) {
      toast.error('Please provide a reason for delay');
      return;
    }

    if (newStatus === 'On Leave') {
      if (!onLeaveData.subordinate_name.trim()) {
        toast.error('Subordinate Name is required');
        return;
      }
      if (!onLeaveData.subordinate_email.trim()) {
        toast.error('Subordinate Email is required');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(onLeaveData.subordinate_email)) {
        toast.error('Please enter a valid email address');
        return;
      }
      if (onLeaveData.subordinate_email.toLowerCase() === user?.email?.toLowerCase()) {
        toast.error('Cannot assign ticket to yourself');
        return;
      }
      if (!onLeaveData.work_description.trim()) {
        toast.error('Work Description is required');
        return;
      }
      setUpdating(true);
      try {
        await ticketAPI.submitOnLeave(ticketId, onLeaveData);
        toast.success('Ticket reassigned to subordinate! Email notification sent.');
        setOnLeaveData({ subordinate_name: '', subordinate_email: '', work_description: '' });
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to submit On Leave reassignment');
      } finally {
        setUpdating(false);
      }
      return;
    }

    if (newStatus === 'Shift Completed') {
      if (!shiftCompletedData.next_shift_name.trim()) {
        toast.error('Next-shift Staff Name is required');
        return;
      }
      if (!shiftCompletedData.next_shift_email.trim()) {
        toast.error('Next-shift Staff Email is required');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(shiftCompletedData.next_shift_email)) {
        toast.error('Please enter a valid email address');
        return;
      }
      if (shiftCompletedData.next_shift_email.toLowerCase() === user?.email?.toLowerCase()) {
        toast.error('Cannot hand over ticket to yourself');
        return;
      }
      if (!shiftCompletedData.comment.trim()) {
        toast.error('Comment is required');
        return;
      }
      setUpdating(true);
      try {
        await ticketAPI.submitShiftCompleted(ticketId, shiftCompletedData);
        toast.success('Ticket handed over to next shift! Notification sent to next-shift staff with CC to Process Owner, L1 & L2 Admin.');
        setShiftCompletedData({ next_shift_name: '', next_shift_email: '', comment: '' });
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to hand over shift');
      } finally {
        setUpdating(false);
      }
      return;
    }

    if (['Purchase Material Requested', 'Purchase Material Approved', 'Purchase Material Received'].includes(newStatus)) {
      if (!materialData.material_name.trim()) {
        toast.error('Purchase Material Name is required');
        return;
      }
      const cost = parseFloat(materialData.estimated_cost);
      if (Number.isNaN(cost) || cost < 0) {
        toast.error('Please enter a valid Estimated Cost');
        return;
      }
      if (!materialData.required_by) {
        toast.error('Please select "By when it is required"');
        return;
      }
      if (newStatus === 'Purchase Material Approved') {
        if (!isMaterialApprover) {
          toast.error('Only Admin panel members, Store Dept PO, or Accounts Dept PO can approve a material purchase.');
          return;
        }
        if (!materialData.decision) {
          toast.error('Please select a decision (Approved / Rejected / Discuss with Ticket Resolver)');
          return;
        }
      }
      setUpdating(true);
      try {
        await ticketAPI.submitMaterialStatus(ticketId, {
          status: newStatus,
          material_name: materialData.material_name,
          estimated_cost: cost,
          required_by: materialData.required_by,
          decision: newStatus === 'Purchase Material Approved' ? materialData.decision : undefined,
          decision_remark: newStatus === 'Purchase Material Approved' ? (materialData.decision_remark || undefined) : undefined,
        });
        if (newStatus === 'Purchase Material Approved') {
          toast.success(`Decision "${materialData.decision}" recorded. Store & Accounts POs notified (CC: Original Resolver, L1 & L2 Admin).`);
        } else {
          toast.success(`Status set to "${newStatus}". One email sent to Store & Accounts Process Owners (CC: Resolver, L1 & L2 Admin).`);
        }
        setMaterialData({ material_name: '', estimated_cost: '', required_by: '', decision: '', decision_remark: '' });
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update material status');
      } finally {
        setUpdating(false);
      }
      return;
    }

    if (newStatus === 'Duplicate Ticket') {
      if (!duplicateData.description.trim()) {
        toast.error('Please provide a description explaining which ticket this duplicates.');
        return;
      }
      setUpdating(true);
      try {
        await ticketAPI.updateTicket(ticketId, {
          status: 'Duplicate Ticket',
          comment: duplicateData.description.trim(),
        });
        toast.success('Ticket marked as Duplicate.');
        setDuplicateData({ description: '' });
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to mark ticket as Duplicate');
      } finally {
        setUpdating(false);
      }
      return;
    }

    if (newStatus === 'Ticket Diverted') {
      if (!divertData.target_department) {
        toast.error('Target Department is required');
        return;
      }
      if (!divertData.resolver_name.trim()) {
        toast.error('Assigned Resolver Name is required');
        return;
      }
      if (!divertData.resolver_email.trim()) {
        toast.error('Assigned Resolver Email is required');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(divertData.resolver_email)) {
        toast.error('Please enter a valid email address');
        return;
      }
      if (divertData.resolver_email.toLowerCase() === user?.email?.toLowerCase()) {
        toast.error('Cannot divert ticket to yourself');
        return;
      }
      if (!divertData.work_description.trim()) {
        toast.error('Work Description is required');
        return;
      }
      if (!divertData.expected_resolution_datetime) {
        toast.error('Expected Resolution Date & Time is required');
        return;
      }
      setUpdating(true);
      try {
        await ticketAPI.divertTicket(ticketId, divertData);
        toast.success('Ticket diverted successfully! Email notification sent.');
        setDivertData({ target_department: '', resolver_name: '', resolver_email: '', work_description: '', expected_resolution_datetime: '' });
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to divert ticket');
      } finally {
        setUpdating(false);
      }
      return;
    }

    setUpdating(true);
    try {
      await ticketAPI.updateTicket(ticketId, {
        status: newStatus !== ticket.status ? newStatus : undefined,
        delay_reason: delayReason || undefined,
        new_expected_date: newExpectedDate || undefined,
        estimated_cost: estimatedCost ? parseFloat(estimatedCost) : undefined,
        comment: comment || undefined
      });
      toast.success('Ticket updated successfully');
      setComment('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update ticket');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveTAT = async () => {
    if (!tatHours) {
      toast.error('Please select a TAT timeline');
      return;
    }
    const parsed = parseInt(tatHours, 10);
    if (![24, 48, 72, 96, 120, 168, 240, 360, 720].includes(parsed)) {
      toast.error('Invalid TAT value');
      return;
    }
    setSavingTat(true);
    try {
      await ticketAPI.setResolutionTAT(ticketId, { tat_hours: parsed });
      toast.success(`TAT set to ${parsed} hours. SLA deadline recalculated.`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save TAT');
    } finally {
      setSavingTat(false);
    }
  };

  const handleRateTicket = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    try {
      await ticketAPI.rateTicket(ticketId, { rating, feedback });
      toast.success('Thank you for your feedback!');
      setShowRatingDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to submit rating');
    }
  };

  // Bold, high-contrast status palette — shared with TC UI-1 / TR UI-1 cards.
  const getStatusBadge = (status) => {
    const variants = {
      'Open': 'bg-blue-600 text-white',
      'Acknowledged': 'bg-cyan-600 text-white',
      'In Progress': 'bg-amber-500 text-white',
      'Reassigned': 'bg-pink-600 text-white',
      'Resolved': 'bg-green-600 text-white',
      'Completed': 'bg-emerald-600 text-white',
      'Closed - Feedback Received': 'bg-teal-600 text-white',
      'SLA BREACHED': 'bg-red-600 text-white animate-pulse',
      'Ticket Diverted': 'bg-violet-600 text-white',
      'On Leave': 'bg-orange-500 text-white',
      'Shift Completed': 'bg-indigo-600 text-white',
      'Purchase Material Requested': 'bg-fuchsia-600 text-white',
      'Purchase Material Approved': 'bg-purple-600 text-white',
      'Purchase Material Received': 'bg-emerald-700 text-white',
      'Long Term Vacations': 'bg-rose-600 text-white',
      'Ticket Closed': 'bg-slate-700 text-white',
      'Duplicate Ticket': 'bg-zinc-600 text-white'
    };
    return (
      <Badge className={`${variants[status] || 'bg-slate-700 text-white'} font-semibold shadow-sm`}>
        {status}
      </Badge>
    );
  };

  // Auto-suggest resolver when divert department changes
  const handleDivertDeptChange = async (dept) => {
    setDivertData(prev => ({ ...prev, target_department: dept }));
    // First try local lookup from preloaded data (handles slash departments correctly)
    const localMatch = divertDepartments.find(d => d.department === dept);
    if (localMatch && localMatch.resolver_name) {
      setDivertData(prev => ({
        ...prev,
        target_department: dept,
        resolver_name: localMatch.resolver_name,
        resolver_email: localMatch.resolver_email,
      }));
      toast.success(`Auto-suggested: ${localMatch.resolver_name} (${localMatch.resolver_email})`);
      return;
    }
    // Fallback to API call for non-slash departments
    setLoadingSuggestion(true);
    try {
      const res = await configAPI.getDivertResolverSuggestion(dept);
      if (res.data?.name) {
        setDivertData(prev => ({
          ...prev,
          target_department: dept,
          resolver_name: res.data.name,
          resolver_email: res.data.email,
        }));
        toast.success(`Auto-suggested: ${res.data.name} (${res.data.email})`);
      }
    } catch (err) {
      // Auto-suggestion is optional; user can fill the field manually
      if (process.env.NODE_ENV !== 'production') {
        console.debug('TicketDetail: staff suggestion lookup failed', err?.message);
      }
    } finally {
      setLoadingSuggestion(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-[#E97832] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Graceful in-page error state — used for 403 access denied and any
  // transient network / 5xx failure. Keeps the user oriented instead of
  // dumping them back to the dashboard with a vague toast.
  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div
          data-testid="ticket-detail-error"
          className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-6 text-center shadow-sm"
        >
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mb-4">
            <AlertCircle className="w-7 h-7 text-amber-600" />
          </div>
          <h2 className="text-lg font-heading font-semibold text-slate-900 mb-2">
            {loadError.type === 'forbidden' ? 'Access not allowed' : 'Could not load this ticket'}
          </h2>
          <p className="text-sm text-slate-600 mb-5">{loadError.message}</p>
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(-1)}
              data-testid="ticket-detail-error-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Back
            </Button>
            {loadError.type !== 'forbidden' && (
              <Button
                size="sm"
                className="bg-[#E97832] hover:bg-[#C25E20] text-white"
                onClick={() => { setLoading(true); setLoadError(null); loadData(); }}
                data-testid="ticket-detail-error-retry"
              >
                Retry
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Locked statuses - ticket is immutable for resolvers. CAO / admin still sees
  // the normal read-only view and can't mutate via TR UI-1 (admin-only endpoints
  // bypass this guard in other UIs).
  const LOCKED_STATUSES = ['Resolved', 'Completed', 'Closed - Feedback Received'];
  const isLocked = ticket?.status && LOCKED_STATUSES.includes(ticket.status);
  const canUpdate = (user?.role === 'process_owner' || user?.role === 'cao') && !isLocked;
  const canRate = user?.role === 'parent_staff' && ticket?.status === 'Resolved' && !ticket?.rating;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <Button 
            data-testid="back-btn"
            variant="ghost" 
            onClick={() => navigate(-1)} 
            size="sm"
            className="mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
            Back
          </Button>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-heading font-bold text-slate-900 font-mono truncate">{ticket?.ticket_id}</h1>
              <p className="text-xs sm:text-sm text-slate-600 truncate">{ticket?.department}</p>
            </div>
            {getStatusBadge(ticket?.status)}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Ticket Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* ─── Assigned-to summary ─── shown to all viewers so resolvers
                    instantly see WHICH department + resolver is responsible. */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="assigned-summary">
                  <h3 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Assigned To
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-blue-600 text-xs uppercase tracking-wide">Department</span>
                      <p className="font-semibold text-blue-900" data-testid="assigned-department">
                        {ticket?.department || '—'}
                      </p>
                      {ticket?.main_department && ticket?.main_department !== ticket?.department && (
                        <p className="text-xs text-blue-700">Main: {ticket.main_department}</p>
                      )}
                      {ticket?.sub_department && (
                        <p className="text-xs text-blue-700">Sub: {ticket.sub_department}</p>
                      )}
                    </div>
                    <div>
                      <span className="text-blue-600 text-xs uppercase tracking-wide">Ticket Resolver</span>
                      {ticket?.assigned_staff_name || ticket?.assigned_staff_email ? (
                        <>
                          <p className="font-semibold text-blue-900" data-testid="assigned-resolver-name">
                            {ticket.assigned_staff_name || '—'}
                          </p>
                          {ticket.assigned_staff_email && (
                            <a
                              href={`mailto:${ticket.assigned_staff_email}`}
                              className="text-xs text-blue-700 underline break-all"
                              data-testid="assigned-resolver-email"
                            >
                              {ticket.assigned_staff_email}
                            </a>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-slate-500 italic">Not yet assigned</p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-1">Description</h3>
                  <p className="text-slate-900">{ticket?.description}</p>
                </div>

                {ticket?.photo_url && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">Attached Photo</h3>
                    <img
                      src={ticket.photo_url}
                      alt="Ticket attachment"
                      className="w-full max-w-md rounded-lg border border-slate-200 shadow-sm"
                      data-testid="ticket-photo"
                    />
                    {(user?.role === 'cao' || user?.is_caredesk_owner) && (() => {
                      const created = ticket.created_at ? new Date(ticket.created_at) : null;
                      const ageHours = created ? (Date.now() - created.getTime()) / 36e5 : 0;
                      const eligible = ageHours >= 72;
                      return (
                        <div className="mt-3 flex items-center gap-3">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!eligible}
                            onClick={async () => {
                              if (!window.confirm('Permanently delete this photo? This cannot be undone.')) return;
                              try {
                                await adminMgmtAPI.purgeTicketPhoto(ticket.ticket_id);
                                toast.success('Photo permanently deleted');
                                loadData();
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || 'Failed to delete photo');
                              }
                            }}
                            className="border-red-200 text-red-700 hover:bg-red-50"
                            data-testid="admin-purge-photo-btn"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Purge Photo (Admin)
                          </Button>
                          <span className="text-xs text-slate-500">
                            {eligible
                              ? 'Eligible — photo is older than 72 hours.'
                              : `Available in ${Math.max(1, Math.ceil(72 - ageHours))} hour(s).`}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                )}
                {!ticket?.photo_url && ticket?.photo_purged_at && (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                    <p className="font-semibold text-slate-700">Photo removed</p>
                    <p className="text-xs">
                      Purged on {new Date(ticket.photo_purged_at).toLocaleString()}
                      {ticket.photo_purged_by ? ` by ${ticket.photo_purged_by}` : ' automatically (24h post-resolution policy)'}.
                    </p>
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-1">Source</h3>
                  <Badge variant="outline">{ticket?.source}</Badge>
                </div>

                {ticket?.delay_reason && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-yellow-800 mb-1">Delay Reason</h3>
                    <p className="text-sm text-yellow-700">{ticket.delay_reason}</p>
                    {ticket.new_expected_date && (
                      <p className="text-xs text-yellow-600 mt-2">
                        New Expected Date: {new Date(ticket.new_expected_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}

                {ticket?.estimated_cost && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-1">Estimated Cost</h3>
                    <p className="text-lg font-bold text-slate-900">₹{ticket.estimated_cost.toLocaleString('en-IN')}</p>
                  </div>
                )}

                {ticket?.status === 'Ticket Diverted' && ticket?.diverted_from_department && (
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-4" data-testid="divert-info-panel">
                    <h3 className="text-sm font-semibold text-violet-800 mb-2 flex items-center gap-2">
                      <Shuffle className="w-4 h-4" />
                      Ticket Diversion Details
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-violet-600">Previous Dept:</span>
                        <p className="font-medium text-violet-900">{ticket.diverted_from_department}</p>
                      </div>
                      <div>
                        <span className="text-violet-600">New Dept:</span>
                        <p className="font-medium text-violet-900">{ticket.diverted_to_department}</p>
                      </div>
                      <div>
                        <span className="text-violet-600">Diverted By:</span>
                        <p className="font-medium text-violet-900">{ticket.diverted_by_name}</p>
                      </div>
                      <div>
                        <span className="text-violet-600">New Resolver:</span>
                        <p className="font-medium text-violet-900">{ticket.assigned_staff_name}</p>
                      </div>
                    </div>
                    {ticket.divert_work_description && (
                      <div className="mt-3 pt-3 border-t border-violet-200">
                        <span className="text-violet-600 text-sm">Work Instructions:</span>
                        <p className="text-sm text-violet-900 whitespace-pre-wrap mt-1">{ticket.divert_work_description}</p>
                      </div>
                    )}
                    {ticket.divert_expected_resolution && (
                      <p className="text-xs text-violet-600 mt-2">
                        Expected Resolution: {ticket.divert_expected_resolution}
                      </p>
                    )}
                  </div>
                )}

                {ticket?.rating && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-green-800 mb-2">Customer Rating</h3>
                    <div className="flex items-center gap-1 mb-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star} 
                          className={`w-5 h-5 ${star <= ticket.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`}
                        />
                      ))}
                      <span className="ml-2 font-semibold text-green-700">{ticket.rating}/5</span>
                    </div>
                    {ticket.feedback && (
                      <p className="text-sm text-green-700">{ticket.feedback}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {ticket?.comments && ticket.comments.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Activity Log
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {ticket.comments.map((c) => (
                      <div key={`comment-${c.timestamp}-${c.user}`} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-semibold text-slate-900">{c.user}</span>
                          <span className="text-xs text-slate-500">
                            {new Date(c.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700">{c.comment}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-base">Requester Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-900">{ticket?.requester_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-700">{ticket?.requester_email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-700">{ticket?.contact}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-700">{ticket?.requester_role}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-base">SLA Tracking</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <SLATimer 
                  createdAt={ticket?.created_at} 
                  breachTime={ticket?.sla_breach_time} 
                  status={ticket?.status}
                />
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-slate-600">Created:</span>
                    <p className="font-mono text-xs">{new Date(ticket?.created_at).toLocaleString()}</p>
                  </div>
                  {ticket?.acknowledged_at && (
                    <div>
                      <span className="text-slate-600">Acknowledged:</span>
                      <p className="font-mono text-xs">{new Date(ticket.acknowledged_at).toLocaleString()}</p>
                    </div>
                  )}
                  {ticket?.resolved_at && (
                    <div>
                      <span className="text-slate-600">Resolved:</span>
                      <p className="font-mono text-xs">{new Date(ticket.resolved_at).toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {canRate && (
              <Card className="border-2 border-[#E97832]/30 bg-orange-50/50">
                <CardContent className="pt-6">
                  <Button 
                    data-testid="rate-ticket-btn"
                    onClick={() => setShowRatingDialog(true)} 
                    className="w-full bg-gradient-to-r from-[#E97832] to-[#EA580C] hover:from-[#C25E20] hover:to-[#C2410C]"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Rate This Service
                  </Button>
                </CardContent>
              </Card>
            )}

            {isLocked && (user?.role === 'process_owner' || user?.role === 'cao') && (
              <Card className="border-emerald-200 bg-emerald-50" data-testid="ticket-locked-notice">
                <CardContent className="p-4 flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-emerald-900">
                    <p className="font-semibold mb-1">Ticket closed – read-only</p>
                    <p>
                      This ticket is marked <strong>{ticket?.status}</strong> and can no longer be
                      modified from the resolver interface. All ticket details remain available
                      for admin panel analytics and monthly reports.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {canUpdate && (
              <Card data-testid="tat-card">
                <CardHeader>
                  <CardTitle className="font-heading text-base">TAT to Resolve the Ticket</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-slate-500">
                    Select how long you will take to resolve this ticket. If the deadline is crossed
                    without an update, an SLA-breach email goes to you and the Level 1 Admin.
                    Post-breach, if nothing is done in the next 24h the Level 2 Admin (CAO) is notified,
                    and 48h post-breach a red-flag email is sent to the Head of School (HOS).
                  </p>
                  <div>
                    <Label>TAT (timeline) *</Label>
                    <Select value={tatHours} onValueChange={setTatHours}>
                      <SelectTrigger data-testid="tat-select">
                        <SelectValue placeholder="Select TAT" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="24" data-testid="tat-24">24 hours</SelectItem>
                        <SelectItem value="48" data-testid="tat-48">48 hours</SelectItem>
                        <SelectItem value="72" data-testid="tat-72">72 hours</SelectItem>
                        <SelectItem value="96" data-testid="tat-96">96 hours</SelectItem>
                        <SelectItem value="120" data-testid="tat-120">5 days</SelectItem>
                        <SelectItem value="168" data-testid="tat-168">7 days</SelectItem>
                        <SelectItem value="240" data-testid="tat-240">10 days</SelectItem>
                        <SelectItem value="360" data-testid="tat-360">15 days</SelectItem>
                        <SelectItem value="720" data-testid="tat-720">1 Month (30 days)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {ticket?.resolution_tat_hours && (
                    <p className="text-xs text-emerald-700" data-testid="tat-current">
                      Current TAT: <span className="font-semibold">{ticket.resolution_tat_hours} hours</span>
                      {ticket.resolution_tat_set_by_name ? ` · set by ${ticket.resolution_tat_set_by_name}` : ''}
                    </p>
                  )}
                  <Button
                    onClick={handleSaveTAT}
                    disabled={savingTat || !tatHours}
                    className="w-full bg-gradient-to-r from-[#0EA5E9] to-[#0369A1] hover:from-[#0284C7] hover:to-[#075985]"
                    data-testid="tat-submit-btn"
                  >
                    {savingTat ? 'Saving...' : 'Submit TAT'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {canUpdate && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-base">Update Ticket</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Status</Label>
                    <Select value={newStatus} onValueChange={setNewStatus}>
                      <SelectTrigger data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Open">Open</SelectItem>
                        <SelectItem value="Acknowledged">Acknowledged</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Resolved">Resolved</SelectItem>
                        <SelectItem value="SLA BREACHED">SLA BREACHED</SelectItem>
                        <SelectItem value="On Leave">On Leave</SelectItem>
                        <SelectItem value="Shift Completed">Shift Completed</SelectItem>
                        <SelectItem value="Purchase Material Requested">Purchase Material Requested</SelectItem>
                        <SelectItem value="Purchase Material Approved">Purchase Material Approved</SelectItem>
                        <SelectItem value="Purchase Material Received">Purchase Material Received</SelectItem>
                        <SelectItem value="Ticket Diverted">Ticket Diverted</SelectItem>
                        <SelectItem value="Long Term Vacations">Long Term Vacations</SelectItem>
                        <SelectItem value="Ticket Closed">Ticket Closed</SelectItem>
                        <SelectItem value="Duplicate Ticket" data-testid="status-duplicate-ticket">Duplicate Ticket</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {newStatus === 'On Leave' && (
                    <div className="space-y-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2 text-sm text-amber-800">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>Reassigning this ticket to your subordinate. SLA timer will continue (no reset). Email notification will be sent to the subordinate with CC to you and Level 1 Admin.</span>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Subordinate Name <span className="text-red-500">*</span></Label>
                        <Input
                          placeholder="Enter subordinate's full name"
                          value={onLeaveData.subordinate_name}
                          onChange={(e) => setOnLeaveData(prev => ({ ...prev, subordinate_name: e.target.value }))}
                          data-testid="on-leave-subordinate-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Subordinate Email ID <span className="text-red-500">*</span></Label>
                        <Input
                          type="email"
                          placeholder="subordinate@mitgurukul.com"
                          value={onLeaveData.subordinate_email}
                          onChange={(e) => setOnLeaveData(prev => ({ ...prev, subordinate_email: e.target.value }))}
                          data-testid="on-leave-subordinate-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Work Description <span className="text-red-500">*</span></Label>
                        <Textarea
                          placeholder="Describe pending work and instructions for the subordinate..."
                          value={onLeaveData.work_description}
                          onChange={(e) => setOnLeaveData(prev => ({ ...prev, work_description: e.target.value }))}
                          rows={4}
                          data-testid="on-leave-work-description"
                        />
                      </div>
                    </div>
                  )}

                  {newStatus === 'Shift Completed' && (
                    <div className="space-y-4 p-4 bg-sky-50 border border-sky-200 rounded-lg">
                      <div className="flex items-start gap-2 text-sm text-sky-800">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>Handing this ticket over to the next-shift staff sub-ordinate. SLA timer continues (no reset). Email will be sent TO the next-shift staff, CC to you (Process Owner), Level 1 Admin and Level 2 Admin.</span>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Next-Shift Staff Name <span className="text-red-500">*</span></Label>
                        <Input
                          placeholder="Enter next-shift staff full name"
                          value={shiftCompletedData.next_shift_name}
                          onChange={(e) => setShiftCompletedData(prev => ({ ...prev, next_shift_name: e.target.value }))}
                          data-testid="shift-completed-next-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Next-Shift Staff Email ID <span className="text-red-500">*</span></Label>
                        <Input
                          type="email"
                          placeholder="nextshift@mitgurukul.com"
                          value={shiftCompletedData.next_shift_email}
                          onChange={(e) => setShiftCompletedData(prev => ({ ...prev, next_shift_email: e.target.value }))}
                          data-testid="shift-completed-next-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Comment on Ticket <span className="text-red-500">*</span></Label>
                        <Textarea
                          placeholder="Summary of work done in this shift and pending items for the next shift..."
                          value={shiftCompletedData.comment}
                          onChange={(e) => setShiftCompletedData(prev => ({ ...prev, comment: e.target.value }))}
                          rows={4}
                          data-testid="shift-completed-comment"
                        />
                      </div>
                    </div>
                  )}

                  {['Purchase Material Requested', 'Purchase Material Approved', 'Purchase Material Received'].includes(newStatus) && (
                    <div className="space-y-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="material-status-form">
                      {newStatus === 'Purchase Material Approved' ? (
                        <div className="flex items-start gap-2 text-sm text-emerald-800">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>
                            Review the material details submitted by the original ticket resolver below and record your decision.
                            Restricted to <strong>Admin panel members</strong>, <strong>Store Dept PO</strong>, and <strong>Accounts Dept PO</strong>.
                            Decision is emailed to Store & Accounts POs with CC to the original resolver, current resolver, L1 and L2 Admins.
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2 text-sm text-emerald-800">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>
                            On submit, this ticket will also be surfaced to <strong>Store Dept</strong> and <strong>Accounts Dept</strong> alongside the current resolver.
                            A single email will be sent <strong>TO</strong> Store & Accounts Process Owners with <strong>CC</strong> to you (Ticket Resolver), Level 1 Admin and Level 2 Admin.
                          </span>
                        </div>
                      )}

                      {newStatus === 'Purchase Material Approved' && !ticket?.material_name && (
                        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="material-approval-missing-request">
                          No prior <strong>Purchase Material Requested</strong> submission found on this ticket. Ask the original ticket resolver to submit the material request first.
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Purchase Material Name <span className="text-red-500">*</span></Label>
                        <Input
                          placeholder="e.g., LED Bulb 15W, PVC Pipe 2 inch..."
                          value={materialData.material_name}
                          onChange={(e) => setMaterialData(prev => ({ ...prev, material_name: e.target.value }))}
                          readOnly={newStatus === 'Purchase Material Approved'}
                          className={newStatus === 'Purchase Material Approved' ? 'bg-white/70 cursor-not-allowed' : ''}
                          data-testid="material-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Estimated Cost of Purchase Material (₹) <span className="text-red-500">*</span></Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Enter estimated cost"
                          value={materialData.estimated_cost}
                          onChange={(e) => setMaterialData(prev => ({ ...prev, estimated_cost: e.target.value }))}
                          readOnly={newStatus === 'Purchase Material Approved'}
                          className={newStatus === 'Purchase Material Approved' ? 'bg-white/70 cursor-not-allowed' : ''}
                          data-testid="material-estimated-cost"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">By When It Is Required <span className="text-red-500">*</span></Label>
                        <Select
                          value={materialData.required_by}
                          onValueChange={(v) => setMaterialData(prev => ({ ...prev, required_by: v }))}
                          disabled={newStatus === 'Purchase Material Approved'}
                        >
                          <SelectTrigger data-testid="material-required-by">
                            <SelectValue placeholder="Select timeline" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1 day" data-testid="required-by-1-day">1 day</SelectItem>
                            <SelectItem value="2 days" data-testid="required-by-2-days">2 days</SelectItem>
                            <SelectItem value="3 days" data-testid="required-by-3-days">3 days</SelectItem>
                            <SelectItem value="4 days" data-testid="required-by-4-days">4 days</SelectItem>
                            <SelectItem value="1 week" data-testid="required-by-1-week">1 week</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {newStatus === 'Purchase Material Approved' && (
                        <div className="space-y-3 pt-2 border-t border-emerald-200" data-testid="material-approval-decision-block">
                          <Label className="text-sm font-semibold text-emerald-900">Approval Decision <span className="text-red-500">*</span></Label>
                          <div className="flex flex-col gap-2">
                            {[
                              { value: 'Approved', label: 'Approved', testid: 'decision-approved' },
                              { value: 'Rejected', label: 'Rejected', testid: 'decision-rejected' },
                              { value: 'Discuss with Ticket Resolver', label: 'Discuss with respective Ticket Resolver', testid: 'decision-discuss' },
                            ].map(opt => (
                              <label
                                key={opt.value}
                                className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                  materialData.decision === opt.value
                                    ? 'border-emerald-500 bg-emerald-100 text-emerald-900'
                                    : 'border-emerald-200 bg-white hover:bg-emerald-50'
                                }`}
                                data-testid={opt.testid}
                              >
                                <input
                                  type="radio"
                                  name="material-decision"
                                  value={opt.value}
                                  checked={materialData.decision === opt.value}
                                  onChange={() => setMaterialData(prev => ({ ...prev, decision: opt.value }))}
                                  className="w-4 h-4 accent-emerald-600"
                                />
                                <span className="text-sm font-medium">{opt.label}</span>
                              </label>
                            ))}
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-emerald-800">Approver Note (optional)</Label>
                            <Textarea
                              rows={2}
                              placeholder="Add a short note for the resolver / store team (optional)"
                              value={materialData.decision_remark}
                              onChange={(e) => setMaterialData(prev => ({ ...prev, decision_remark: e.target.value }))}
                              data-testid="material-decision-remark"
                            />
                          </div>
                          {!isMaterialApprover && (
                            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2" data-testid="material-approval-not-authorized">
                              You are not authorized to submit an approval decision. Please request an Admin / Store PO / Accounts PO to do this.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {newStatus === 'Duplicate Ticket' && (
                    <div className="space-y-3 p-4 bg-zinc-50 border border-zinc-200 rounded-lg" data-testid="duplicate-ticket-form">
                      <div className="flex items-start gap-2 text-sm text-zinc-800">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>
                          Mark this ticket as a <strong>Duplicate</strong> of another existing ticket. Describe which ticket this duplicates (ID and brief context) so admins can cross-reference and archive cleanly.
                        </span>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Description <span className="text-red-500">*</span></Label>
                        <Textarea
                          rows={4}
                          placeholder="e.g., This is a duplicate of VGWS-2026-0123 — same fan issue reported earlier this week."
                          value={duplicateData.description}
                          onChange={(e) => setDuplicateData({ description: e.target.value })}
                          data-testid="duplicate-description-input"
                        />
                      </div>
                    </div>
                  )}

                  {newStatus === 'Ticket Diverted' && (
                    <div className="space-y-4 p-4 bg-violet-50 border border-violet-200 rounded-lg">
                      <div className="flex items-start gap-2 text-sm text-violet-800">
                        <Shuffle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>Diverting this ticket to a different department. SLA timer will continue (no reset). Email notification will be sent to the new resolver with CC to you and Level 1 Admin.</span>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Select Target Department <span className="text-red-500">*</span></Label>
                        <Select value={divertData.target_department} onValueChange={handleDivertDeptChange}>
                          <SelectTrigger data-testid="divert-target-department">
                            <SelectValue placeholder="Select department" />
                          </SelectTrigger>
                          <SelectContent className="max-h-64">
                            {divertDepartments.length > 0 ? (
                              divertDepartments.map((d) => (
                                <SelectItem key={d.department} value={d.department}>{d.department.replace(/_/g, ' ')}</SelectItem>
                              ))
                            ) : (
                              [
                                "EYP_Academics","PYP_Academics","MYP_Academics","DP_Academics","CP_Academics",
                                "2/2_Boarding","4/2_Boarding","5/2_Boarding","6/2_Boarding",
                                "2/3_Performing_Arts_Center","Sports","University_Placement_Cell (UPO)",
                                "Students_Well_Being_and_Support_Counseling_and_Special_Education",
                                "Admissions","Design","IT","Accounts","HR",
                                "Estate_Infrastucture_Maintance","PIS","Cleaning","Gardening",
                                "Security","Refractory","Medical","Store","Electrical","AC",
                                "Plumbing","Staff_Quarters","Transportation","Student_Section"
                              ].map((dept) => (
                                <SelectItem key={dept} value={dept}>{dept.replace(/_/g, ' ')}</SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      {loadingSuggestion && (
                        <div className="flex items-center gap-2 text-xs text-violet-600">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          <span>Loading suggested resolver...</span>
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Assigned Resolver Name <span className="text-red-500">*</span></Label>
                        <Input
                          placeholder="Enter resolver's full name"
                          value={divertData.resolver_name}
                          onChange={(e) => setDivertData(prev => ({ ...prev, resolver_name: e.target.value }))}
                          data-testid="divert-resolver-name"
                        />
                        {divertData.resolver_name && divertData.target_department && (
                          <p className="text-xs text-violet-600">Auto-suggested from department data (editable)</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Assigned Resolver Email ID <span className="text-red-500">*</span></Label>
                        <Input
                          type="email"
                          placeholder="resolver@mitgurukul.com"
                          value={divertData.resolver_email}
                          onChange={(e) => setDivertData(prev => ({ ...prev, resolver_email: e.target.value }))}
                          data-testid="divert-resolver-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Work Description <span className="text-red-500">*</span></Label>
                        <Textarea
                          placeholder="Describe pending work and instructions for the new resolver..."
                          value={divertData.work_description}
                          onChange={(e) => setDivertData(prev => ({ ...prev, work_description: e.target.value }))}
                          rows={4}
                          data-testid="divert-work-description"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Expected Resolution Date & Time <span className="text-red-500">*</span></Label>
                        <Input
                          type="datetime-local"
                          value={divertData.expected_resolution_datetime}
                          onChange={(e) => setDivertData(prev => ({ ...prev, expected_resolution_datetime: e.target.value }))}
                          data-testid="divert-expected-resolution"
                        />
                      </div>
                    </div>
                  )}

                  {newStatus === 'SLA BREACHED' && (
                    <>
                      <div>
                        <Label>Delay Reason *</Label>
                        <Select value={delayReason} onValueChange={setDelayReason}>
                          <SelectTrigger data-testid="delay-reason-select">
                            <SelectValue placeholder="Select reason" />
                          </SelectTrigger>
                          <SelectContent>
                            {delayReasons.map((reason) => (
                              <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>New Expected Date</Label>
                        <Input 
                          data-testid="new-expected-date"
                          type="datetime-local" 
                          value={newExpectedDate} 
                          onChange={(e) => setNewExpectedDate(e.target.value)}
                        />
                      </div>
                    </>
                  )}

                  {newStatus !== 'On Leave' && newStatus !== 'Shift Completed' && newStatus !== 'Ticket Diverted' && newStatus !== 'Duplicate Ticket' && !['Purchase Material Requested', 'Purchase Material Approved', 'Purchase Material Received'].includes(newStatus) && (
                    <>
                      <div>
                        <Label>Estimated Cost (₹)</Label>
                        <Input 
                          data-testid="estimated-cost-input"
                          type="number" 
                          placeholder="Enter cost" 
                          value={estimatedCost} 
                          onChange={(e) => setEstimatedCost(e.target.value)}
                        />
                      </div>

                      <div>
                        <Label>Add Comment</Label>
                        <Textarea 
                          data-testid="comment-textarea"
                          placeholder="Add a comment..." 
                          value={comment} 
                          onChange={(e) => setComment(e.target.value)}
                          rows={3}
                        />
                      </div>
                    </>
                  )}

                  <Button 
                    data-testid="update-ticket-btn"
                    onClick={handleUpdate} 
                    disabled={updating} 
                    className={`w-full ${newStatus === 'On Leave' ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700' : newStatus === 'Shift Completed' ? 'bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700' : ['Purchase Material Requested','Purchase Material Approved','Purchase Material Received'].includes(newStatus) ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700' : newStatus === 'Ticket Diverted' ? 'bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700' : newStatus === 'Duplicate Ticket' ? 'bg-gradient-to-r from-zinc-600 to-zinc-700 hover:from-zinc-700 hover:to-zinc-800' : 'bg-gradient-to-r from-[#E97832] to-[#EA580C] hover:from-[#C25E20] hover:to-[#C2410C]'}`}
                  >
                    {updating ? 'Updating...' : newStatus === 'On Leave' ? 'Submit On Leave' : newStatus === 'Shift Completed' ? 'Hand Over to Next Shift' : newStatus === 'Ticket Diverted' ? 'Divert Ticket' : newStatus === 'Purchase Material Approved' ? 'Submit Approval Decision' : ['Purchase Material Requested','Purchase Material Received'].includes(newStatus) ? 'Submit Material Update' : newStatus === 'Duplicate Ticket' ? 'Mark as Duplicate' : 'Update Ticket'}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <Dialog open={showRatingDialog} onOpenChange={setShowRatingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rate Our Service</DialogTitle>
            <DialogDescription>
              How satisfied are you with the resolution of your complaint?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  data-testid={`star-${star}`}
                  onClick={() => setRating(star)}
                  className="transition-transform hover:scale-110"
                >
                  <Star 
                    className={`w-10 h-10 cursor-pointer ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300 hover:text-yellow-200'}`}
                  />
                </button>
              ))}
            </div>
            <div>
              <Label>Feedback (Optional)</Label>
              <Textarea 
                data-testid="feedback-textarea"
                placeholder="Share your experience..." 
                value={feedback} 
                onChange={(e) => setFeedback(e.target.value)}
                rows={3}
              />
            </div>
            <Button 
              data-testid="submit-rating-btn"
              onClick={handleRateTicket} 
              className="w-full bg-gradient-to-r from-[#E97832] to-[#EA580C] hover:from-[#C25E20] hover:to-[#C2410C]"
            >
              Submit Rating
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketDetail;
